// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { createContext, useContext } from 'react';

import type { ConnectionStatus } from '../../api/events';
import type { AuthUser, CaseRole, Health } from '../../api/types';
import type { UseThemeResult } from '../useTheme';

/**
 * What `AppShell` resolves once per case and both frames (the Bygning shell
 * and the expert ToolFrame) render from: the health handshake, the case's
 * display name, the events connection, the single `useTheme()` instance and
 * the signed-in user.
 */
export interface CaseShell {
  health: Health | undefined;
  /** `GET /health` failed: the server is unreachable. */
  unreachable: boolean;
  /** The record name, else the `.rux` file name; undefined while loading. */
  projectName: string | undefined;
  connection: ConnectionStatus;
  activeJobCount: number;
  theme: UseThemeResult;
  user: AuthUser | undefined;
  logout: (() => void) | undefined;
  /** Signed in through a share link: bound to this case, no account pages, no case list. */
  viaShareLink: boolean;
  role: CaseRole | null | undefined;
}

const CaseShellContext = createContext<CaseShell | null>(null);

export const CaseShellProvider = CaseShellContext.Provider;

export function useCaseShell(): CaseShell {
  const shell = useContext(CaseShellContext);
  if (!shell) throw new Error('useCaseShell must be used inside <AppShell>');
  return shell;
}
